import { useState, useEffect, useRef } from 'react';
import { useCartDiscount, CartBeat } from '../../../hooks/useCartDiscount';

interface FloatingCartButtonProps {
  cartItems: CartBeat[];
  onCartOpen: () => void;
  hasNowPlaying: boolean;
}

interface ToastData {
  message: string;
  sub: string;
  tier: 'triple' | 'ultimate';
}

export default function FloatingCartButton({
  cartItems,
  onCartOpen,
  hasNowPlaying,
}: FloatingCartButtonProps) {
  const [isPulsing, setIsPulsing] = useState(false);
  const [isScaled, setIsScaled] = useState(false);
  const [toast, setToast] = useState<ToastData | null>(null);
  const [toastVisible, setToastVisible] = useState(false);

  const prevCountRef = useRef(cartItems.length);
  const prevPaidRef = useRef(0);
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { paidItemsCount } = useCartDiscount(cartItems);

  // Pulse + bounce when a new item is added
  useEffect(() => {
    if (cartItems.length > prevCountRef.current) {
      setIsPulsing(true);
      setIsScaled(true);

      const pingTimer = setTimeout(() => setIsPulsing(false), 1000);
      const scaleTimer = setTimeout(() => setIsScaled(false), 300);

      prevCountRef.current = cartItems.length;
      return () => {
        clearTimeout(pingTimer);
        clearTimeout(scaleTimer);
      };
    }
    prevCountRef.current = cartItems.length;
    return undefined;
  }, [cartItems.length]);

  // Bundle unlocked toast
  useEffect(() => {
    const prev = prevPaidRef.current;
    const curr = paidItemsCount;

    let newToast: ToastData | null = null;

    if (prev < 3 && curr >= 3 && curr < 5) {
      newToast = {
        message: 'Triple Bundle Unlocked!',
        sub: '1 beat is now FREE — cheapest one\'s on us!',
        tier: 'triple',
      };
    } else if (prev < 5 && curr >= 5) {
      newToast = {
        message: 'Ultimate Bundle Unlocked!',
        sub: '3 beats are now FREE — you\'re saving big!',
        tier: 'ultimate',
      };
    }

    prevPaidRef.current = curr;

    if (newToast) {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);

      setToast(newToast);
      // Small delay so opacity transition kicks in after mount
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setToastVisible(true));
      });

      toastTimerRef.current = setTimeout(() => {
        setToastVisible(false);
        hideTimerRef.current = setTimeout(() => setToast(null), 400);
      }, 4200);
    }

    return () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    };
  }, [paidItemsCount]);

  if (cartItems.length === 0) return null;

  const btnBottom = hasNowPlaying ? 'bottom-24' : 'bottom-6';
  const toastBottom = hasNowPlaying ? 'bottom-44' : 'bottom-24';

  return (
    <>
      {/* Bundle Unlocked Toast */}
      {toast && (
        <div
          className={`fixed right-6 z-50 transition-all duration-400 ${toastBottom} ${
            toastVisible
              ? 'opacity-100 translate-y-0'
              : 'opacity-0 translate-y-3'
          }`}
          style={{ transitionDuration: '350ms' }}
        >
          <div
            className={`relative flex items-center gap-3 pl-4 pr-10 py-3.5 rounded-2xl text-white max-w-[280px] ${
              toast.tier === 'ultimate'
                ? 'bg-gradient-to-br from-amber-500 via-orange-500 to-red-600'
                : 'bg-gradient-to-br from-red-600 to-rose-700'
            }`}
          >
            {/* Shimmer overlay */}
            <div className="absolute inset-0 rounded-2xl overflow-hidden pointer-events-none">
              <div
                className="absolute inset-0 opacity-20"
                style={{
                  background:
                    'linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.7) 50%, transparent 60%)',
                  animation: 'shimmer 1.5s ease-in-out',
                }}
              />
            </div>

            {/* Icon */}
            <div className="w-9 h-9 flex items-center justify-center flex-shrink-0 bg-white/25 rounded-full">
              {toast.tier === 'ultimate' ? (
                <i className="ri-vip-crown-2-fill text-lg text-yellow-200"></i>
              ) : (
                <i className="ri-gift-2-fill text-lg"></i>
              )}
            </div>

            {/* Text */}
            <div className="flex-1 min-w-0">
              <p className="font-bold text-sm leading-tight">{toast.message}</p>
              <p className="text-xs text-white/80 mt-0.5 leading-snug">{toast.sub}</p>
            </div>

            {/* Close */}
            <button
              onClick={() => {
                setToastVisible(false);
                hideTimerRef.current = setTimeout(() => setToast(null), 400);
              }}
              className="absolute top-2 right-2 w-5 h-5 flex items-center justify-center text-white/60 hover:text-white transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-sm"></i>
            </button>
          </div>

          {/* Pointer arrow pointing down toward the button */}
          <div className="flex justify-end pr-[26px]">
            <div
              className={`w-3 h-3 rotate-45 -mt-1.5 ${
                toast.tier === 'ultimate' ? 'bg-red-600' : 'bg-rose-700'
              }`}
            />
          </div>
        </div>
      )}

      {/* Floating Cart Button */}
      <div className={`fixed right-6 z-40 ${btnBottom}`}>
        {/* Ping ring — appears on item add */}
        {isPulsing && (
          <span className="absolute inset-0 rounded-full bg-red-500 animate-ping" />
        )}

        <button
          onClick={onCartOpen}
          title="View Cart"
          className="relative w-14 h-14 flex items-center justify-center bg-red-600 text-white rounded-full hover:bg-red-700 cursor-pointer whitespace-nowrap"
          style={{
            transform: isScaled ? 'scale(1.18)' : 'scale(1)',
            transition: 'transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1), background-color 0.2s ease',
            boxShadow: '0 4px 20px rgba(220,38,38,0.45)',
          }}
        >
          <i className="ri-shopping-cart-2-fill text-xl"></i>

          {/* Count badge */}
          <span
            className="absolute -top-1.5 -right-1.5 w-5 h-5 flex items-center justify-center bg-gray-900 text-white text-xs font-bold rounded-full"
            style={{ fontSize: '10px' }}
          >
            {cartItems.length > 9 ? '9+' : cartItems.length}
          </span>
        </button>
      </div>

      {/* Shimmer keyframe */}
      <style>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(200%); }
        }
      `}</style>
    </>
  );
}
